import { useQuery } from "@tanstack/react-query";
import type { Product } from "@/api/types";
import { api, call, xClient } from "@/api/spec";
import { qk } from "@/api/queryKeys";
import { platform } from "@/platform";
import { setKnownStorefront } from "@/platform/storefront";
import { formatMoney, productPrice } from "./money";

const STALE_MS = 5 * 60 * 1000;

/**
 * 첫 결제 특가 상태(시안 43:734 배너 · 749:1486 부족 시트 안내 줄 공용 규칙). 특가(`.first`) 행이 있으면 active,
 * 모든 행이 같은 할인율의 특가면 allRate = 그 할인율(→ "모든 상품 N% 할인"), 아니면 null.
 */
export function firstPurchaseOffer(items: readonly StoreItem[]): { active: boolean; allRate: number | null } {
  const firsts = items.filter((i) => i.product.firstPurchaseOnly);
  const rates = new Set(firsts.map((i) => i.product.discountPercent));
  const allRate = firsts.length > 0 && firsts.length === items.length && rates.size === 1 ? [...rates][0] ?? null : null;
  return { active: firsts.length > 0, allRate };
}

/** 상점에 그릴 상품 한 줄: 서버 상품 + 표시 가격 + 결제 가능 여부. 첫 결제 특가면 원가(짝 일반 상품) 표시 가격 */
export type StoreItem = { product: Product; priceLabel: string; purchasable: boolean; regularPriceLabel: string | null };
export type StoreProducts = {
  /** 적용 국가(앱 = 스토어 계정 국가 힌트, 서버가 최종 판정) */
  region: string | null;
  items: StoreItem[];
  /** 기기 스토어 조회 실패 — 서버 목록·가격은 보여 주되 결제 버튼은 잠긴다(결정 8) */
  storeUnavailable: boolean;
};

/**
 * 상점 목록 (상점·결제 설계 결정 8·15). 1) 기기 스토어 국가 → 2) GET /store/products?region= → 3) 기기 getProducts 로 대조.
 * - 노출 = 서버 목록 ∩ 스토어 목록(스토어에 없는 상품은 숨김). 웹·모의처럼 스토어 가격이 없으면(priceString null) 서버 가격 행을 표시한다.
 * - 첫 결제 특가(AC0-258): 서버가 고른 `.first` 의 짝 일반 상품(regular)도 함께 조회해 원가를 스토어 가격으로 표시한다(없으면 서버 가격 행).
 * - 가격 = 스토어 priceString 우선(실제 청구액과 일치), 없으면 서버 가격 행.
 * - 스토어 조회가 실패하면 서버 목록 전체를 서버 가격으로 보여 주고 purchasable=false.
 * staleTime 5m(기존 useProducts 와 같음). 스토어 국가는 사용자가 바꿀 수 있어 질의마다 다시 읽는다.
 */
export function useStoreProducts(enabled = true) {
  return useQuery({
    queryKey: qk.products,
    enabled,
    staleTime: STALE_MS,
    queryFn: async (): Promise<StoreProducts> => {
      const region = platform.clientKind === "web" ? null : await platform.billing.getStorefront();
      setKnownStorefront(region);
      const products = await call(api.GET("/store/products", { params: { header: xClient(), query: region ? { region } : {} } }));
      const regularFallback = (p: Product) => (p.regular ? formatMoney(p.regular.price) : null);
      const fallback = (p: Product): StoreItem => ({ product: p, priceLabel: productPrice(p), purchasable: false, regularPriceLabel: regularFallback(p) });
      if (platform.clientKind === "web") return { region, items: products.map((p) => ({ ...fallback(p), purchasable: true })), storeUnavailable: false };
      let inStore: Map<string, { priceString: string | null }>;
      try {
        const ids = products.flatMap((p) => (p.regular ? [p.productId, p.regular.productId] : [p.productId]));
        inStore = new Map((await platform.billing.getProducts(ids)).map((s) => [s.productId, s]));
      } catch {
        return { region, items: products.map(fallback), storeUnavailable: true };
      }
      return {
        region,
        storeUnavailable: false,
        items: products.flatMap((p) => {
          const s = inStore.get(p.productId);
          if (!s) return [];
          const regularPriceLabel = p.regular ? (inStore.get(p.regular.productId)?.priceString ?? formatMoney(p.regular.price)) : null;
          return [{ product: p, priceLabel: s.priceString ?? productPrice(p), purchasable: true, regularPriceLabel }];
        }),
      };
    },
  });
}
